import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { projectInput, requestProposal } from './aiWriting';
import SoldeCreditsIA from './SoldeCreditsIA';

/**
 * Aide à la rédaction d'une rubrique de « Détails du projet » (tâche bourse_rubrique).
 * L'IA s'appuie sur toute la fiche ; le porteur relit puis utilise ou ignore le texte.
 */
const AiFieldAssistant = ({ field, label, formData, countryName, onApply }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);

  const ready = ((formData[field] || '') + (formData.description || '')).replace(/\s/g, '').length >= 20;

  const ask = async () => {
    setLoading(true);
    setError(null);
    setProposal(null);
    try {
      const result = await requestProposal('bourse_rubrique', { ...projectInput(formData, countryName), field });
      setProposal(result.proposal);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    onApply(proposal.text);
    setProposal(null);
  };

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={ask}
        disabled={loading || !ready}
        title={ready ? `Proposer une rédaction de la rubrique « ${label} »` : 'Écrivez d\'abord quelques mots dans cette rubrique ou dans la description du projet'}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <SparklesIcon className="h-4 w-4" />
        {loading ? 'Rédaction en cours…' : 'Rédiger avec l\'IA'}
      </button>
      <SoldeCreditsIA tache="bourse_rubrique" />

      {error && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {proposal && (
        <div className="mt-2 border border-primary-200 bg-primary-50 rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
            Proposition de l'IA — relisez-la avant de l'utiliser
          </p>
          {proposal.text
            ? <p className="text-sm text-gray-800 whitespace-pre-line">{proposal.text}</p>
            : <p className="text-sm text-gray-600">L'IA n'a pas assez d'éléments pour rédiger cette rubrique.</p>}
          {proposal.tips?.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {proposal.tips.map(tip => <li key={tip}>{tip}</li>)}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {proposal.text && (
              <button
                type="button"
                onClick={apply}
                className="px-3 py-1.5 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
              >
                Utiliser ce texte
              </button>
            )}
            <button
              type="button"
              onClick={() => setProposal(null)}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50"
            >
              {proposal.text ? 'Ignorer' : 'Fermer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiFieldAssistant;
